import { useEffect, useState } from 'react';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { SnackbarManager } from 'fluxo-ui';

import { ROUTES, STORAGE_KEYS } from './common/constants';
// Layouts
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import { BlockLoading } from './components/Loading';
import AuthLayout from './layouts/AuthLayout';
import PreAuthLayout from './layouts/PreAuthLayout';
// Pre-auth pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SignupPage from './pages/SignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Auth pages
import AnalyticsPage from './pages/AnalyticsPage';
import CategoryPage from './pages/CategoryPage';
import DashboardPage from './pages/DashboardPage';
import ExamListPage from './pages/ExamListPage';
import ExamManagementPage from './pages/ExamManagementPage';
import LiveQuizPage from './pages/LiveQuizPage';
import LiveQuizPlayPage from './pages/LiveQuizPlayPage';
import NotFoundPage from './pages/NotFoundPage';
import OrganizationPage from './pages/OrganizationPage';
import OrganizationsListPage from './pages/OrganizationsListPage';
import ProfilePage from './pages/ProfilePage';
import QuestionBankPage from './pages/QuestionBankPage';
import QuestionCreatePage from './pages/QuestionCreatePage';
import QuestionEditPage from './pages/QuestionEditPage';
import QuestionsPendingPage from './pages/QuestionsPendingPage';
import CertificatesPage from './pages/CertificatesPage';
import FlagsReviewPage from './pages/FlagsReviewPage';
import ResourcesPage from './pages/ResourcesPage';
import ResultsPage from './pages/ResultsPage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import SettingsPage from './pages/SettingsPage';
import TakeExamPage from './pages/TakeExamPage';
import UsersPage from './pages/UsersPage';
// Protected Route Component
import { useAuthStore } from './store/authStore';
import { useUIStore } from './store/uiStore';

function App() {
    const { refreshUser } = useAuthStore();
    const { initTheme } = useUIStore();
    const [isInitializing, setIsInitializing] = useState(true);

    useEffect(() => {
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(STORAGE_KEYS.THEME);

        initTheme();

        const initAuth = async () => {
            const storedToken = sessionStorage.getItem('testam_auth_token');
            if (storedToken) {
                try {
                    await refreshUser();
                } catch (error) {
                    console.error('Failed to restore session:', error);
                }
            }
            setIsInitializing(false);
        };

        initAuth();
    }, [initTheme, refreshUser]);

    if (isInitializing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <BlockLoading text="Loading Testam..." size="lg" />
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <SnackbarManager />
            <BrowserRouter>
                <Routes>
                    {/* Pre-auth routes */}
                    <Route element={<PreAuthLayout />}>
                        <Route path={ROUTES.HOME} element={<HomePage />} />
                        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                        <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
                        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
                        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />
                        {/* Public certificate verification */}
                        <Route path="/verify/:code" element={<VerifyCertificatePage />} />
                        <Route path="/verify" element={<VerifyCertificatePage />} />
                    </Route>

                    {/* Live quiz players — public standalone layout, guests welcome */}
                    <Route path="/live-quiz/join" element={<LiveQuizPlayPage />} />

                    {/* Take Exam — standalone layout (no sidebar/header) */}
                    <Route
                        path={ROUTES.TAKE_EXAM}
                        element={
                            <ProtectedRoute>
                                <TakeExamPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Authenticated routes */}
                    <Route
                        element={
                            <ProtectedRoute>
                                <AuthLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
                        <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
                        <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />

                        {/* Question routes */}
                        <Route path="/questions" element={<QuestionBankPage />} />
                        <Route path="/questions/create" element={<QuestionCreatePage />} />
                        <Route path="/questions/pending" element={<QuestionsPendingPage />} />
                        <Route
                            path="/questions/flags"
                            element={
                                <ProtectedRoute requiredRoles={['super_admin', 'org_admin', 'org_moderator']}>
                                    <FlagsReviewPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/questions/:id/edit" element={<QuestionEditPage />} />

                        {/* Exam routes */}
                        <Route path={ROUTES.EXAMS} element={<ExamListPage />} />
                        <Route path="/exams/create" element={<ExamManagementPage />} />
                        <Route path="/exams/:id/edit" element={<ExamManagementPage />} />

                        {/* Results */}
                        <Route path="/results" element={<ResultsPage />} />
                        <Route path={ROUTES.RESULTS} element={<ResultsPage />} />
                        <Route path="/exams/:examId/results" element={<ResultsPage />} />

                        {/* Live Quiz hosting */}
                        <Route path="/live-quiz" element={<LiveQuizPage />} />

                        {/* Organization & Users */}
                        <Route
                            path="/organizations"
                            element={
                                <ProtectedRoute requiredRoles={['super_admin']}>
                                    <OrganizationsListPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/organization" element={<OrganizationPage />} />
                        <Route
                            path="/organizations/:orgId"
                            element={
                                <ProtectedRoute requiredRoles={['super_admin']}>
                                    <OrganizationPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <ProtectedRoute requiredRoles={['super_admin', 'org_admin', 'org_moderator']}>
                                    <UsersPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Categories */}
                        <Route path="/categories" element={<CategoryPage />} />

                        {/* Certificates */}
                        <Route path="/certificates" element={<CertificatesPage />} />

                        {/* Resources */}
                        <Route path="/resources" element={<ResourcesPage />} />

                        {/* Analytics */}
                        <Route path="/analytics" element={<AnalyticsPage />} />
                    </Route>

                    {/* 404 Page */}
                    <Route path="/404" element={<NotFoundPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
